import React from 'react'
import PropTypes from 'prop-types'

import { connect } from 'react-redux'
import { actions as mapFilterActions } from 'store/map-filters'

import { get, isObject, isEmpty } from 'lodash'
import { Typography } from '@material-ui/core'

import { Switch, Route, Redirect } from 'react-router-dom'

import { ErrorBoundary } from '@scoir/error-reporter'
import FourOhFourRoute from 'routes/status/FourOhFour'

import getMapLocation from './get-map-location.js'
import FilterBar from './FilterBar'
import MapRoute from './ExploreMap'
import ListRoute from './ExploreList'
import { HighSchoolExplorerOptIn } from 'components/HighSchoolExplorerEarlyAccess'
import { isHighSchoolExplorerEnabled } from 'store/feature-toggle'

import './index.scss'

const GOOGLE_CONSTANT = 156543.03392
const generateRadius = (lat, zoom, width = 1024, height = 1024) => {
  const METERS_PER_PX = (GOOGLE_CONSTANT * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)
  return METERS_PER_PX * Math.max(width, height) * 0.66
}

const momentToQueryParam = (val) => {
  if (isObject(val) && val.format) {
    return val.format('YYYY-MM-DD')
  }
  return val
}

const ZOOM_LEVEL_FOR_LOCATION_SELECT = 13

export class ExploreRoute extends React.Component {
  state = {
    rendered: false,
  }

  getDataFilters() {
    const { latitude, longitude, zoom, mapFilters } = this.props
    return {
      radius: generateRadius(
        latitude,
        zoom,
        get(this.containerRef, 'offsetWidth'),
        get(this.containerRef, 'offsetHeight'),
      ),
      latitude,
      longitude,
      maxSize: mapFilters.maxSize,
      minSize: mapFilters.minSize,
      category: mapFilters.category,
      isActive: mapFilters.isActive,
      virtual: mapFilters.virtual,
      startDate: momentToQueryParam(get(mapFilters, 'visitRange.from')),
      endDate: momentToQueryParam(get(mapFilters, 'visitRange.to')),
    }
  }

  onMapChange(mapState) {
    const updatedFilters = {
      ...this.props.mapFilters,
      latitude: mapState.latitude,
      longitude: mapState.longitude,
      zoom: mapState.zoom || this.props.zoom,
    }
    this.props.updateStoredFilters(updatedFilters)
  }

  onHighSchoolClear() {
    const changeObj = {
      ...this.props.mapFilters,
      selectedSchoolId: undefined,
    }
    this.props.updateStoredFilters(changeObj)
  }

  storeContainerRef(ref) {
    if (!this.containerRef) {
      this.containerRef = ref
      this.setState({
        rendered: true,
      })
    }
  }

  onChangeSelectedSchool(selectedSchoolId, latitude, longitude, shouldZoom) {
    const lat = shouldZoom ? latitude : this.props.latitude
    const lng = shouldZoom ? longitude : this.props.longitude
    const zoom = shouldZoom ? ZOOM_LEVEL_FOR_LOCATION_SELECT : this.props.zoom
    const coordinates = {
      latitude: lat,
      longitude: lng,
      zoom,
    }
    const changeObj = {
      ...this.props.mapFilters,
      ...coordinates,
      selectedSchoolId,
    }
    this.props.updateStoredFilters(changeObj)
  }
  updateFilters(filters) {
    if (isEmpty(filters.minSize)) {
      delete filters.minSize
    }
    if (isEmpty(filters.maxSize)) {
      delete filters.maxSize
    }
    this.props.updateStoredFilters(filters)
  }
  buildAccessibilityString(dataFilters) {
    const publicOrPrivate = dataFilters.category !== undefined ? dataFilters.category : 'Not Selected'
    const onlyScoirSchools = dataFilters.isActive === true ? 'true' : 'false'
    const seniorClassMin = dataFilters.minSize ? dataFilters.minSize : 'not set'
    const seniorClassMax = dataFilters.maxSize ? dataFilters.maxSize : 'not set'
    const availabilityDate =
      dataFilters.startDate && dataFilters.endDate
        ? `from ${dataFilters.startDate} to ${dataFilters.endDate}`
        : 'not selected'
    const inPersonOrVirtual =
      dataFilters.virtual !== undefined ? (dataFilters.virtual ? 'Virtual' : 'In Person') : 'not selected'
    const path = window.location.pathname.split('/')
    const view = path[path.length - 1]
    return `
      Category: ${publicOrPrivate};
      Only Scoir Schools: ${onlyScoirSchools};
      Senior Classs Size: Minimum size ${seniorClassMin}, Maximum size ${seniorClassMax};
      Availability: ${availabilityDate};
      In Person or Virtual: ${inPersonOrVirtual};
      View: ${view}
    `
  }
  render() {
    const ROOT_PATH = import.meta.env.BASE_URL
    const selectedSchoolId = get(this.props.mapFilters, 'selectedSchoolId')
    const dataFilters = this.getDataFilters()
    const accessibilityString = this.buildAccessibilityString(dataFilters)
    return (
      <div
        className="route-explore"
        ref={(ref) => this.storeContainerRef(ref)}
      >
        {this.props.showHighSchoolExplorerOptIn && <HighSchoolExplorerOptIn />}
        <ErrorBoundary>
          <div className="content-wrapper">
            <Typography
              variant="srOnly"
              aria-live="polite"
              aria-atomic="true"
            >
              {accessibilityString}
            </Typography>
            {this.state.rendered && (
              <Switch>
                <Route
                  exact
                  path={`${ROOT_PATH}/recruit/explore/map`}
                  render={(props) => (
                    <>
                      <FilterBar
                        filters={this.props.mapFilters}
                        onChange={(filters) => this.updateFilters(filters)}
                      />
                      <MapRoute
                        {...props}
                        lat={this.props.latitude}
                        lng={this.props.longitude}
                        zoom={this.props.zoom}
                        dataFilters={dataFilters}
                        selectedHighSchoolId={selectedSchoolId}
                        onMapChange={(mapState) => this.onMapChange(mapState)}
                        onHighSchoolClear={() => this.onHighSchoolClear()}
                        onHighSchoolSelect={(selectedSchoolId, latitude, longitude, shouldZoom) => {
                          this.onChangeSelectedSchool(selectedSchoolId, latitude, longitude, shouldZoom)
                        }}
                      />
                    </>
                  )}
                />
                <Route
                  exact
                  path={`${ROOT_PATH}/recruit/explore/list`}
                  render={(props) => (
                    <>
                      <FilterBar
                        filters={this.props.mapFilters}
                        onChange={(filters) => this.updateFilters(filters)}
                      />
                      <ListRoute
                        {...props}
                        selectedHighSchoolId={selectedSchoolId}
                        dataFilters={dataFilters}
                        onHighSchoolSelect={(selectedSchoolId, latitude, longitude) => {
                          this.onChangeSelectedSchool(selectedSchoolId, latitude, longitude, true)
                        }}
                      />
                    </>
                  )}
                />
                <Redirect
                  exact
                  from={`${ROOT_PATH}/recruit/explore`}
                  to={`${ROOT_PATH}/recruit/explore/map`}
                />
                <FourOhFourRoute />
              </Switch>
            )}
          </div>
        </ErrorBoundary>
      </div>
    )
  }
}

ExploreRoute.propTypes = {
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  zoom: PropTypes.number.isRequired,
  mapFilters: PropTypes.object.isRequired,
  updateStoredFilters: PropTypes.func.isRequired,
}

const ConnectedExploreRoute = connect(
  (store) => {
    const mapFilters = get(store.mapFilters, 'record', {})
    const preference = get(store.user, 'record.RecruitMapPreference')
    const collegeCoords = get(store.college.record, 'Location.coordinates')
    const { latitude, longitude, zoom } = getMapLocation(mapFilters, preference, collegeCoords)
    return {
      latitude,
      longitude,
      zoom,
      mapFilters,
      showHighSchoolExplorerOptIn: isHighSchoolExplorerEnabled(store),
    }
  },
  {
    updateStoredFilters: mapFilterActions.updateFilters,
  },
)(ExploreRoute)

export default ConnectedExploreRoute
